import { createFileRoute } from "@tanstack/react-router";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import { TaskLayout } from "lib/components/builders/communities/tasks/TaskLayout";
import { getLocationBreadcrumbs } from "lib/components/builders/communities/utils/locationUtils";
import { useEstimationTask } from "lib/components/builders/communities/utils/useTasksAPI";
import { FetchErrorMessage } from "lib/components/errors/FetchErrorMessage";
import { getBuilderLocationParentsURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { locationTasksPath, useAppRouterParams, taskPath } from "lib/routeHelpers";
import { useMemo } from "react";
import Loading from "ui/components/Loading";
import withAuthentication from "../../../../../components/withAuthentication";

function TaskPageReady() {
  const { builderId, taskId } = useAppRouterParams();

  if (!builderId || !taskId) {
    return null;
  }

  return <TaskPage builderId={builderId} taskId={taskId} />;
}

function TaskPage({ builderId, taskId }: { builderId: string; taskId: string }) {
  const {
    data: estimationTask,
    loading: loadingEstimationTask,
    error: errorEstimationTask,
    mutate: mutateEstimationTask,
  } = useEstimationTask({ builderId, taskId });

  const { data: parents, error: errorParents } = useFetch<ClientLocationWithLevel[]>(
    estimationTask ? getBuilderLocationParentsURL({ builderId, locationId: estimationTask.locationId }) : "",
    [builderId, estimationTask],
    {
      handleErrorLocally: true,
    },
  );

  const breadcrumbs = useMemo(() => {
    if (!estimationTask) {
      return [];
    }

    return [
      ...getLocationBreadcrumbs(parents, estimationTask.locationId, builderId),
      {
        text: "Jobs",
        href: locationTasksPath(builderId, estimationTask.locationId),
      },
      {
        text: estimationTask?.name ?? "",
        href: taskPath(builderId, taskId),
      },
    ];
  }, [builderId, estimationTask?.name, estimationTask?.locationId, parents, taskId]);

  return (
    <BuilderPortalLayout
      selected="locations"
      pageTitle={estimationTask?.name ?? "Loading Estimation Job..."}
      header={estimationTask?.name}
      loadingContent={loadingEstimationTask}
      breadcrumbs={breadcrumbs}
      wide
    >
      {loadingEstimationTask && <Loading />}
      {errorParents && <FetchErrorMessage fetchError={errorParents} />}
      {errorEstimationTask && <FetchErrorMessage fetchError={errorEstimationTask} />}
      {estimationTask && <TaskLayout estimationTask={estimationTask} onUpdateEstimationTask={mutateEstimationTask} />}
    </BuilderPortalLayout>
  );
}

export const Route = createFileRoute("/builders/$builder_id/tasks/$task_id/")({
  component: withAuthentication(TaskPageReady, { features: ["estimationTasks"] }),
});
